import React, { useState, useCallback, useEffect } from 'react';

interface SearchBarProps {
  onSearch: (query: string, isRegex: boolean) => void;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  placeholder = "Search paths (supports regex)..." 
}) => {
  const [query, setQuery] = useState('');
  const [isRegex, setIsRegex] = useState(false);
  const [isValidRegex, setIsValidRegex] = useState(true);

  const validateRegex = useCallback((pattern: string): boolean => {
    if (!isRegex || !pattern) return true;
    try {
      new RegExp(pattern, 'i');
      return true;
    } catch {
      return false;
    }
  }, [isRegex]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    
    const isValid = validateRegex(value);
    setIsValidRegex(isValid);
    
    if (isValid) {
      onSearch(value, isRegex);
    }
  }, [onSearch, isRegex, validateRegex]);

  const handleRegexToggle = useCallback(() => {
    const newIsRegex = !isRegex;
    setIsRegex(newIsRegex);
    
    const isValid = validateRegex(query);
    setIsValidRegex(isValid);
    
    if (isValid) {
      onSearch(query, newIsRegex);
    }
  }, [isRegex, query, onSearch, validateRegex]);

  const handleClear = useCallback(() => {
    setQuery('');
    setIsValidRegex(true);
    onSearch('', isRegex);
  }, [onSearch, isRegex]);

  useEffect(() => {
    const isValid = validateRegex(query);
    setIsValidRegex(isValid);
  }, [query, isRegex, validateRegex]);

  return (
    <div className="mb-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={`w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-2.5 border rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 transition-all duration-200 font-body text-on-surface placeholder:text-outline/70 ${
              !isValidRegex
                ? 'border-error focus:ring-error/30 focus:border-error bg-error-container/20'
                : 'border-white/10 focus:ring-primary-container/20 focus:border-primary-container bg-surface-container-lowest/75'
            }`}
          />
          
          <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
            <svg 
              className={`w-3 sm:w-4 h-3 sm:h-4 ${!isValidRegex ? 'text-error' : 'text-outline'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
              />
            </svg>
          </div>
          
          {query && (
            <button
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-2 sm:pr-3 flex items-center text-outline hover:text-primary-container transition-colors duration-200"
            >
              <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <button
          onClick={handleRegexToggle}
          className={`sm:ml-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-code font-semibold uppercase tracking-[0.14em] transition-all duration-200 ${
            isRegex
              ? 'bg-primary-container text-on-primary shadow-[0_0_18px_rgba(0,242,255,0.18)]'
              : 'bg-white/[0.03] text-outline hover:bg-primary-container/10 hover:text-primary-container border border-white/10'
          }`}
          title={isRegex ? 'Disable regex mode' : 'Enable regex mode'}
        >
          <span className="hidden sm:inline">.*</span>
          <span className="sm:hidden">RegEx</span>
        </button>
      </div>
      
      {!isValidRegex && (
        <div className="mt-2 p-2 bg-error-container/20 border border-error/35 rounded-xl">
          <p className="text-xs sm:text-sm text-error">
            <span className="font-medium">Invalid regex pattern:</span> Please check your regular expression syntax
          </p>
        </div>
      )}
      
      {query && isValidRegex && (
        <div className="mt-2 text-xs text-on-surface-variant">
          <span className="hidden sm:inline">Searching for: </span>
          <span className="font-code font-medium break-all text-primary-container">{query}</span>
          {isRegex && <span className="ml-1 sm:ml-2 px-1 sm:px-2 py-1 bg-primary-container/10 text-primary-container border border-primary-container/25 rounded text-xs">REGEX</span>}
        </div>
      )}
    </div>
  );
};

export default SearchBar;